import { CircularProgress, Typography } from '@mui/material'
import {
  useCallback, useEffect, useMemo, useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { actions } from '../../../RestockingState'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'src/utils/store'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import FormBatchQuantity from 'src/features/restocking/components/RestockingWebAppForm/components/FormBatchQuantity/FormBatchQuantity'
import Page from 'src/features/restocking/components/RestockingWebAppForm/components/Page/Page'
import TextLabel from 'src/features/restocking/components/RestockingWebAppForm/components/TextLabel/TextLabel'
import UserGuidance from 'src/features/restocking/components/RestockingWebAppForm/components/UserGuidance/UserGuidance'
import UserIdentifier from 'src/features/restocking/components/RestockingWebAppForm/components/UserIdentifier/UserIdentifier'
import theme from 'src/styles/theme'
import ReportQuantityPerBoxDivergentModal from 'src/features/restocking/components/RestockingWebAppForm/components/ReportQuantityPerBoxDivergentModal/ReportQuantityPerBoxDivergentModal'
import * as S from './CollectBatchPage.styles'
import FormAddNewBatches from 'src/features/restocking/components/RestockingWebAppForm/components/FormAddNewBatches/FormAddNewBatches'
import { NewBatchForEmptyGondola } from 'src/features/restocking/RestockingTypes'
import { formatDateReplaceHour } from 'src/utils/formatDate'

interface FormData {
  quantity: number | null;
}

const CollectBatchPage: React.FC = () => {
  const {
    register,
    watch,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>()
  const [hasError, setHasError] = useState(false)
  const [openModalConfirmationQuantity, setOpenModalConfirmationQuantity] = useState(false)
  const [openModalFullCart, setOpenModalFullCart] = useState(false)
  const [openModalReportQuantityPerBoxDivergent, setOpenModalReportQuantityPerBoxDivergent] = useState(false)
  const [quantityConfirmed, setQuantityConfirmed] = useState(false)
  const { currentCollectRestockingAddress, user, restockingConfig } = useSelector((state) => state.restocking)
  const { fc } = useFulfillmentCenter()
  const dispatch = useDispatch()
  const quantity = watch('quantity')
  const history = useHistory()
  const [isDisabled, setIsDisabled] = useState(false)

  const [newBatches, setNewBatches] = useState<NewBatchForEmptyGondola[]>([])
  const [addBatchesMode, setAddBatchesMode] = useState(false)
  const newBatchesTotalQuantity = newBatches.reduce((acc, batch) => acc + batch.quantity, 0)

  const closeConfirmationModal = () => setOpenModalConfirmationQuantity(false)
  const closeModalFullCart = () => setOpenModalFullCart(false)
  const closeModalReportQuantityPerBoxDivergent = () => setOpenModalReportQuantityPerBoxDivergent(false)

  useEffect(() => {
    if (currentCollectRestockingAddress?.done) {
      dispatch(actions.setAddressStartedPage(currentCollectRestockingAddress))
      dispatch(actions.clearProperty('currentCollectRestockingAddress'))

      const hasSourceAddress = user.startedPage.itemsToCollect?.addresses.find(
        (address) =>
          !address.done && address.order !== currentCollectRestockingAddress.order,
      )

      if (hasSourceAddress) {
        dispatch(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: hasSourceAddress,
        }))

        history.push('/restocking/webapp/collect-address')
      }
    }
  }, [currentCollectRestockingAddress, history, user, dispatch])

  const currentProduct = currentCollectRestockingAddress?.items.find((item) => !item.done)

  useEffect(() => {
    if (!currentProduct && currentCollectRestockingAddress) {
      dispatch(actions.setRestockingAddressIsDone(currentCollectRestockingAddress.order))
    }
  }, [currentProduct, currentCollectRestockingAddress, dispatch])

  const currentBatch = currentProduct?.batches.find((batch) => !batch.done)

  useEffect(() => {
    if (!currentBatch && currentProduct && currentCollectRestockingAddress) {
      dispatch(actions.setRestockingItemIsDone({ addressOrder: currentCollectRestockingAddress.order }))
    }

    setNewBatches([])
  }, [currentBatch, currentProduct, currentCollectRestockingAddress, dispatch])

  const onSubmit = useCallback(() => {
    if (isDisabled || !currentBatch || !currentProduct) {
      return
    }

    if (currentBatch.id && quantity) {
      setIsDisabled(true)

      if (quantity < currentBatch.quantity && quantity > 0 && !quantityConfirmed) {
        setOpenModalConfirmationQuantity(true)
        setIsDisabled(false)
        return
      }

      if (quantity > currentBatch.quantity) {
        setHasError(true)
        setError('quantity', { message: 'Quantidade maior que o lote!' })
        setIsDisabled(false)
        return
      }

      if (quantity < 0) {
        setHasError(true)
        setError('quantity', { message: 'Quantidade não pode ser negativa!' })
        setIsDisabled(false)
        return
      }

      const itemAddress = {
        idItem: currentProduct.id,
        idsItemAddress: currentProduct.itemAddress.ids,
        quantity,
        quantityToCollect: currentProduct.itemAddress.quantity,
      }

      if (user.startedPage.itemsToCollect?.id && currentCollectRestockingAddress) {
        const restockingBatchIsDone = {
          addressOrder: currentCollectRestockingAddress.order,
          idItem: currentProduct.id,
          idBatch: currentBatch.id,
        }

        dispatch(actions.finishRestockingAddress({
          itemAddress,
          badge: user.badge,
          idPage: user.startedPage.itemsToCollect.id,
          restockingBatchIsDone,
          idFulfillmentCenter: fc.id,
          idModel: currentProduct.idModel,
          history,
          setIsDisabled,
        }))
      } else {
        setIsDisabled(false)
      }

      setQuantityConfirmed(false)
      reset({ quantity: null })
    } else if (!currentBatch.id) {
      if (restockingConfig?.isExactDueDate && quantity) {
        setIsDisabled(true)

        const quantityNumber = Number(quantity)

        if (quantityNumber > currentBatch.quantity) {
          setHasError(true)
          setError('quantity', { message: 'Quantidade maior que o lote!' })
          setIsDisabled(false)
          return
        }

        if (quantityNumber < 0) {
          setHasError(true)
          setError('quantity', { message: 'Quantidade não pode ser negativa!' })
          setIsDisabled(false)
          return
        }

        if (user.startedPage.itemsToCollect?.id && currentCollectRestockingAddress) {
          const restockingBatchIsDone = {
            addressOrder: currentCollectRestockingAddress.order,
            idItem: currentProduct.id,
            idBatch: currentBatch.id,
          }

          dispatch(actions.finishCollectAddressEmptyGondola({
            idFulfillmentCenter: fc.id,
            idPage: user.startedPage.itemsToCollect.id,
            idItem: currentProduct.id,
            idItemAddress: currentProduct.itemAddress.ids[0],
            quantityToCollect: currentProduct.itemAddress.quantity,
            batches: quantityNumber > 0 ? [{
              quantity: quantityNumber,
              expirationDate: currentBatch.expirationDate,
            }] : [],
            badge: user.badge,
            restockingBatchIsDone,
            idModel: currentProduct.idModel,
            history,
            setIsDisabled,
          }))
        } else {
          setIsDisabled(false)
        }

        reset({ quantity: null })
      } else if (!restockingConfig?.isExactDueDate) {
        setIsDisabled(true)

        if (user.startedPage.itemsToCollect?.id && currentCollectRestockingAddress) {
          if (newBatches.length === 0 && !quantityConfirmed) {
            setOpenModalConfirmationQuantity(true)
            setIsDisabled(false)
            return
          }

          const restockingBatchIsDone = {
            addressOrder: currentCollectRestockingAddress.order,
            idItem: currentProduct.id,
            idBatch: currentBatch.id,
          }

          dispatch(actions.finishCollectAddressEmptyGondola({
            idFulfillmentCenter: fc.id,
            idPage: user.startedPage.itemsToCollect.id,
            idItem: currentProduct.id,
            idItemAddress: currentProduct.itemAddress.ids[0],
            quantityToCollect: currentProduct.itemAddress.quantity,
            batches: newBatches,
            badge: user.badge,
            restockingBatchIsDone,
            idModel: currentProduct.idModel,
            history,
            setIsDisabled,
          }))
        } else {
          setIsDisabled(false)
        }

        setQuantityConfirmed(false)
      }
    }
  }, [
    currentBatch,
    currentProduct,
    quantity,
    quantityConfirmed,
    user,
    currentCollectRestockingAddress,
    fc,
    history,
    reset,
    setError,
    dispatch,
    isDisabled,
    restockingConfig,
    newBatches,
  ])

  useEffect(() => {
    if (quantityConfirmed) {
      onSubmit()
    }
  }, [onSubmit, quantityConfirmed])

  const onSubmitConfirmationQuantity = () => {
    setQuantityConfirmed(true)
    setOpenModalConfirmationQuantity(false)
  }

  const onSubmitFullCart = () => {
    if (!user.startedPage.itemsToCollect || !currentCollectRestockingAddress) return

    const idsItemAddress = currentCollectRestockingAddress.items.flatMap((item) => {
      if (!item.done) return item.itemAddress.ids
      return []
    })

    dispatch(actions.restockingFullcart({
      badge: user.badge,
      idPage: user.startedPage.itemsToCollect.id,
      history,
      idsItemAddress,
    }))

    setOpenModalFullCart(false)
  }

  const formatQuantity = (qty: number) => `${qty} ${qty > 1 ? 'unidades' : 'unidade'}`

  const getModalConfirmationQuantityInfo = () => {
    if (currentBatch) {
      if (currentBatch.id && quantity) {
        return {
          title: 'Confirmação quantidade',
          description: (
            <S.ContainerDescription>
              <p>
                A quantidade para separar é <strong>{formatQuantity(currentBatch.quantity)}</strong>,
                porém está sendo enviado <strong>{formatQuantity(quantity)}</strong>.
              </p>
              <p>
                Deseja prosseguir?
              </p>
            </S.ContainerDescription>
          ),
        }
      }
      if (!currentBatch.id && restockingConfig && !restockingConfig.isExactDueDate) {
        return {
          title: 'Nenhum lote encontrado',
          description: (
            <S.ContainerDescription>
              <p>
                Ao prosseguir, você assume que nenhum lote a partir da validade <strong>{formatDateReplaceHour(currentBatch.expirationDate)}</strong> foi encontrado nessa localização.
              </p>
              <p>
                Deseja prosseguir?
              </p>
            </S.ContainerDescription>
          ),
        }
      }
    }
    return null
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const modalConfirmationQuantityInfo = useMemo(() => getModalConfirmationQuantityInfo(), [currentBatch, quantity])

  return (
    <Page headerTitle='Reposição' goBackRoute='/restocking/webapp/collect-product'>
      <S.Info>
        <UserIdentifier idPage={user.startedPage.itemsToCollect?.id}/>
        {!addBatchesMode && (
        <UserGuidance>
          <TextLabel
            width='100%'
            height='3.2rem'
            backgroundColor={theme.colors.green4}
          >
            <Typography style={{ fontWeight: 'bold', fontSize: '30px' }}>
              {currentCollectRestockingAddress?.address}
            </Typography>
          </TextLabel>
        </UserGuidance>
        )}
        <UserGuidance>
          <TextLabel
            width='100%'
            height='3.2rem'
            backgroundColor={currentProduct?.dueDateType === 'LONG_DURATION' ? theme.colors.yellow : theme.colors.green4}
          >
            <Typography style={{ fontWeight: 'bold', fontSize: '13px' }}>
              {currentProduct?.description}
            </Typography>
          </TextLabel>
        </UserGuidance>
      </S.Info>
      {!addBatchesMode ? (
        <S.Form onSubmit={handleSubmit(onSubmit)}>
          {currentBatch && (
            <FormBatchQuantity
              productDueDateType={currentProduct?.dueDateType}
              quantity={currentBatch.quantity}
              expirationDate={currentBatch.expirationDate}
              register={register}
              inputName='quantity'
              dataTestId='input-quantity-restocking-test-id'
              hasError={hasError}
              errorMessage={errors.quantity?.message}
              useInput={!!currentBatch.id || (!currentBatch.id && !restockingConfig?.collectInEmptyGondola)}
              countedQuantity={newBatchesTotalQuantity}
            />
          )}
          <S.ContainerButton>
            {(currentBatch && !currentBatch.id && restockingConfig && restockingConfig.collectInEmptyGondola) && (
            <S.CustomButton
              title={newBatches.length === 0 ? 'Adicionar lotes' : 'Editar lotes'}
              bgColor='blue'
              hoverColor='darkBlueHover'
              letterColor='white'
              data-testid='add-batches-button-test-id'
              disabled={isDisabled}
              onClick={() => setAddBatchesMode(true)}
            />
            )}
            <S.CustomButton
              startIcon={isDisabled && <CircularProgress size={26} />}
              title={isDisabled ? 'Aguarde...' : 'Próximo'}
              type='submit'
              bgColor='lightBlue5'
              hoverColor='darkBlueHover'
              letterColor='white'
              disabledColor='lightBlue6'
              data-testid='next-button-test-id'
              disabled={isDisabled || (currentBatch?.id && !quantity) || (!currentBatch?.id && !quantity && !restockingConfig?.collectInEmptyGondola)}
            />
            <S.CustomButton
              title='Carrinho cheio'
              bgColor='orange2'
              hoverColor='orange'
              letterColor='white'
              disabledColor='lightBlue6'
              data-testid='full-cart-button-test-id'
              onClick={() => setOpenModalFullCart(true)}
            />
            {restockingConfig?.reportQuantityPerBox && (
            <S.CustomButton
              title='Reportar caixaria divergente'
              bgColor='red'
              hoverColor='redHover'
              letterColor='white'
              data-testid='report-quantity-per-box-divergent-button-test-id'
              onClick={() => setOpenModalReportQuantityPerBoxDivergent(true)}
            />
            )}
          </S.ContainerButton>
          {modalConfirmationQuantityInfo && (
          <ConfirmOrRejectModal
            show={openModalConfirmationQuantity}
            title={modalConfirmationQuantityInfo.title}
            customizableDescription={modalConfirmationQuantityInfo.description}
            onConfirm={onSubmitConfirmationQuantity}
            onReject={closeConfirmationModal}
            onClose={closeConfirmationModal}
            dataTestId='modal-confirmation-not-found-quantity-test-id'
          />
          )}
          <ConfirmOrRejectModal
            show={openModalFullCart}
            title='Confirmação carrinho cheio'
            description='Confirme que o carrinho está cheio.'
            onConfirm={onSubmitFullCart}
            onReject={closeModalFullCart}
            onClose={closeModalFullCart}
            dataTestId='modal-confirmation-full-cart-test-id'
          />
        </S.Form>
      ) : (
        <FormAddNewBatches
          batches={newBatches}
          setBatches={setNewBatches}
          quantityToCollect={currentBatch?.quantity!}
          totalQuantity={newBatchesTotalQuantity}
          goBackAction={() => setAddBatchesMode(false)}
          minExpirationDate={currentBatch?.expirationDate!}
        />
      )}
      {currentProduct && (
      <ReportQuantityPerBoxDivergentModal
        show={openModalReportQuantityPerBoxDivergent}
        productDescription={currentProduct.description}
        badge={user.badge}
        idItem={currentProduct.id}
        onClose={closeModalReportQuantityPerBoxDivergent}
      />
      )}
    </Page>
  )
}

export default CollectBatchPage
